import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Save, Trash2, X, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useReloadBus } from "@/lib/reloadBus";

/**
 * Iter88zbv (2026-02-19) — Niederlassungen-Verwaltung.
 * Ebene unterhalb des Mandanten mit eigenen Vertriebsaufschlägen,
 * Rabatt-Grenzen, PDF-Ko-Branding und Mehrverkauf-Split-Schlüssel.
 */

const inputCls = "w-full h-11 px-3 rounded-xl bg-[#09090B] border border-zinc-700 text-zinc-100 text-[14px] focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none";
const labelCls = "block text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-1.5";

function BranchForm({ initial, onCancel, onSaved }) {
  const empty = {
    name: "", code: "", active: true,
    default_rep_margin_type: "percent",
    default_rep_margin_value: "",
    default_max_discount_percent: "",
    default_max_discount_eur: "",
    logo_url: "", brand_color: "", pdf_header_line: "",
    upsell_split_rep_pct: "",
  };
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Name muss mindestens 2 Zeichen haben"); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      default_rep_margin_value: form.default_rep_margin_value === "" ? null : Number(form.default_rep_margin_value),
      default_max_discount_percent: form.default_max_discount_percent === "" ? null : Number(form.default_max_discount_percent),
      default_max_discount_eur: form.default_max_discount_eur === "" ? null : Number(form.default_max_discount_eur),
      upsell_split_rep_pct: form.upsell_split_rep_pct === "" ? null : Number(form.upsell_split_rep_pct),
    };
    try {
      const r = isEdit
        ? await api.patch(`/branches/${initial.id}`, payload)
        : await api.post(`/branches`, payload);
      toast.success(isEdit ? "Aktualisiert" : "Angelegt");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-white">
          {isEdit ? "Niederlassung bearbeiten" : "Neue Niederlassung"}
        </h2>
        <button type="button" onClick={onCancel} className="h-9 w-9 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center text-zinc-300">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Name *</label>
          <input data-testid="branch-form-name" className={inputCls} value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="z. B. Niederlassung Nord" />
        </div>
        <div>
          <label className={labelCls}>Kürzel</label>
          <input data-testid="branch-form-code" className={inputCls} value={form.code || ""} onChange={(e) => upd("code", e.target.value.toUpperCase())} placeholder="NORD" maxLength={20} />
        </div>
        <div>
          <label className={labelCls}>Aktiv</label>
          <select className={inputCls} value={form.active ? "yes" : "no"} onChange={(e) => upd("active", e.target.value === "yes")}>
            <option value="yes">Ja</option>
            <option value="no">Nein (verborgen)</option>
          </select>
        </div>
      </div>

      <div className="pt-3 border-t border-zinc-800">
        <h3 className="font-mono text-[11px] uppercase tracking-wider text-[#00E575] mb-3">Vertriebsaufschlag & Rabatte</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Marge-Typ</label>
            <select className={inputCls} value={form.default_rep_margin_type || "percent"} onChange={(e) => upd("default_rep_margin_type", e.target.value)}>
              <option value="percent">Prozent</option>
              <option value="eur">Euro (absolut)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Marge-Wert</label>
            <input data-testid="branch-form-margin" type="number" step="0.1" className={inputCls} value={form.default_rep_margin_value || ""} onChange={(e) => upd("default_rep_margin_value", e.target.value)} placeholder={form.default_rep_margin_type === "eur" ? "1500" : "17"} />
          </div>
          <div>
            <label className={labelCls}>&nbsp;</label>
            <div className="h-11 flex items-center text-[12px] text-zinc-500 leading-tight">
              Standard für alle Reps in dieser Niederlassung
            </div>
          </div>
          <div>
            <label className={labelCls}>Max. Rabatt %</label>
            <input type="number" step="0.1" className={inputCls} value={form.default_max_discount_percent || ""} onChange={(e) => upd("default_max_discount_percent", e.target.value)} placeholder="5" />
          </div>
          <div>
            <label className={labelCls}>Max. Rabatt €</label>
            <input type="number" step="1" className={inputCls} value={form.default_max_discount_eur || ""} onChange={(e) => upd("default_max_discount_eur", e.target.value)} placeholder="500" />
          </div>
          <div />
        </div>
      </div>

      <div className="pt-3 border-t border-zinc-800">
        <h3 className="font-mono text-[11px] uppercase tracking-wider text-[#00E575] mb-3">Mehrverkauf-Split (Projektierung & Betreuung)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Vertrieb-Anteil (%)</label>
            <input data-testid="branch-form-split" type="number" min="0" max="100" step="1" className={inputCls} value={form.upsell_split_rep_pct || ""} onChange={(e) => upd("upsell_split_rep_pct", e.target.value)} placeholder="50 (= 50/50)" />
          </div>
          <div className="flex items-end text-[12px] text-zinc-400 leading-snug pb-2">
            {form.upsell_split_rep_pct
              ? `Vertrieb: ${form.upsell_split_rep_pct}% · Novatus: ${100 - Number(form.upsell_split_rep_pct || 0)}%`
              : "Rest geht an Novatus (Standard 50/50 wenn leer bei Provision)."}
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-zinc-800">
        <h3 className="font-mono text-[11px] uppercase tracking-wider text-[#00E575] mb-3">Ko-Branding (Angebots-PDF)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={labelCls}>PDF-Kopfzeile</label>
            <input className={inputCls} value={form.pdf_header_line || ""} onChange={(e) => upd("pdf_header_line", e.target.value)} placeholder="Novatus · Niederlassung Nord" />
          </div>
          <div>
            <label className={labelCls}>Akzent-Farbe (Hex)</label>
            <input className={inputCls} value={form.brand_color || ""} onChange={(e) => upd("brand_color", e.target.value)} placeholder="#00E575" maxLength={16} />
          </div>
          <div>
            <label className={labelCls}>Logo-URL</label>
            <input className={inputCls} value={form.logo_url || ""} onChange={(e) => upd("logo_url", e.target.value)} placeholder="https://…" />
          </div>
        </div>
      </div>

      <div className="pt-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 font-medium text-sm">Abbrechen</button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="branch-form-save"
          className="h-11 px-5 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isEdit ? "Speichern" : "Anlegen"}
        </button>
      </div>
    </div>
  );
}

export default function AdminBranches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | {id, ...}

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/branches");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Niederlassungen nicht laden");
    } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useReloadBus("*", () => load(true));

  const del = async (b) => {
    if (!window.confirm(`Niederlassung "${b.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader title="Niederlassungen" back="/admin" />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold text-white flex items-center gap-2">
              <Building2 size={22} className="text-[#00E575]" /> Niederlassungen
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Vertriebszweige mit eigenen Aufschlägen und Ko-Branding — Katalog bleibt bei Novatus.
            </p>
          </div>
          {!editing && (
            <button
              type="button"
              data-testid="branch-new-btn"
              onClick={() => setEditing({})}
              className="h-11 px-5 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-sm inline-flex items-center gap-2"
            >
              <Plus size={16} /> Neue Niederlassung
            </button>
          )}
        </div>

        {editing && (
          <BranchForm
            initial={editing.id ? editing : null}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); }}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="animate-spin mr-2" /> Lade…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-zinc-500 text-sm bg-[#18181B] border border-zinc-800 rounded-2xl">
            Noch keine Niederlassungen. Über &bdquo;Neue Niederlassung&ldquo; oben rechts anlegen.
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((b) => (
              <div key={b.id} data-testid={`branch-row-${b.id}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 flex flex-wrap items-center gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{
                    backgroundColor: (b.brand_color || "#00E575") + "22",
                    color: b.brand_color || "#00E575",
                  }}
                >
                  {(b.code || b.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-100 truncate flex items-center gap-2">
                    {b.name}
                    {!b.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 uppercase">inaktiv</span>}
                  </div>
                  <div className="text-[12px] text-zinc-400 flex items-center gap-3 flex-wrap mt-0.5">
                    {b.default_rep_margin_value != null && (
                      <span>Marge {b.default_rep_margin_value}{b.default_rep_margin_type === "eur" ? " €" : " %"}</span>
                    )}
                    {b.upsell_split_rep_pct != null && (
                      <span>Split {b.upsell_split_rep_pct}/{100 - b.upsell_split_rep_pct}</span>
                    )}
                    <span className="inline-flex items-center gap-1"><Users size={11} />{b.rep_count} Rep{b.rep_count === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <button type="button" onClick={() => setEditing(b)} className="h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 text-sm font-medium">
                  Bearbeiten
                </button>
                <button
                  type="button"
                  data-testid={`branch-delete-${b.id}`}
                  onClick={() => del(b)}
                  disabled={b.rep_count > 0}
                  title={b.rep_count > 0 ? `${b.rep_count} Reps zugeordnet — erst umhängen` : "Löschen"}
                  className="h-9 w-9 rounded-lg text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
